import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";

const AuthContext = createContext(null);

async function claimPendingAssessment() {
  const raw = localStorage.getItem("ckb_assessment");
  if (!raw) return;
  try {
    const a = JSON.parse(raw);
    if (a.id && a.claim_token && !a.claimed) {
      await api.post("/assessment/claim", { id: a.id, claim_token: a.claim_token });
      localStorage.setItem("ckb_assessment", JSON.stringify({ ...a, claimed: true }));
    }
  } catch (_) { /* ignore */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = signed out
  const { setStoredLang } = useI18n();

  const applyUser = useCallback((u) => {
    setUser(u || false);
    localStorage.setItem("ckb_session", u ? "1" : "0");
    if (u?.language) setStoredLang(u.language);
    if (u) claimPendingAssessment();
  }, [setStoredLang]);

  const check = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      applyUser(data.user);
    } catch (_) {
      try {
        const { data } = await api.post("/auth/refresh");
        applyUser(data.user);
      } catch (_e) {
        applyUser(false);
      }
    }
  }, [applyUser]);

  useEffect(() => {
    // Skip while returning from Google OAuth; AuthCallback establishes the session first.
    if (window.location.hash?.includes("session_id=")) return;
    if (localStorage.getItem("ckb_session") === "0") { setUser(false); return; }
    check();
  }, [check]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) { /* ignore */ }
    applyUser(false);
  };

  return <AuthContext.Provider value={{ user, setUser: applyUser, refresh: check, logout }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
